import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { SectionLoader, EmptyState } from "@/components/common";
import { initials } from "@/lib/format";
import { externalUrl, displayUrl } from "@/lib/url";
import { RoleBadges } from "@/components/RoleBadges";
import { Search, Mail, Phone, Globe, Linkedin, MapPin, Briefcase, Users } from "lucide-react";

function MemberDialog({ member, onClose }) {
  if (!member) return null;
  const b = member.business;
  return (
    <Dialog open={!!member} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <div className="flex items-center gap-4">
            <Avatar className="h-16 w-16"><AvatarImage src={member.avatar || undefined} /><AvatarFallback className="bg-primary text-lg text-primary-foreground">{initials(member.name)}</AvatarFallback></Avatar>
            <div>
              <DialogTitle className="font-serif text-xl">{member.name}</DialogTitle>
              <p className="text-sm text-muted-foreground">{[member.title, member.company].filter(Boolean).join(" · ")}</p>
              <RoleBadges roles={member.roles} is_owner={member.is_owner} showEligible={false} size="sm" className="mt-1.5" />
            </div>
          </div>
        </DialogHeader>
        {member.bio && <p className="text-sm leading-6 text-muted-foreground">{member.bio}</p>}
        <div className="space-y-2 text-sm">
          {member.email && <a href={`mailto:${member.email}`} className="flex items-center gap-2 text-foreground hover:text-primary"><Mail className="h-4 w-4 text-muted-foreground" />{member.email}</a>}
          {member.phone && <div className="flex items-center gap-2 text-foreground"><Phone className="h-4 w-4 text-muted-foreground" />{member.phone}</div>}
          {member.location_text && <div className="flex items-center gap-2 text-foreground"><MapPin className="h-4 w-4 text-muted-foreground" />{member.location_text}</div>}
          {member.website && <a href={externalUrl(member.website)} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-foreground hover:text-primary"><Globe className="h-4 w-4 text-muted-foreground" />{displayUrl(member.website)}</a>}
          {member.linkedin && <a href={externalUrl(member.linkedin)} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-foreground hover:text-primary"><Linkedin className="h-4 w-4 text-muted-foreground" />LinkedIn</a>}
        </div>
        {b && (
          <div className="rounded-xl border border-border bg-secondary/50 p-4">
            <div className="flex items-center gap-2"><Briefcase className="h-4 w-4 text-primary" /><span className="font-semibold text-foreground">{b.business_name}</span>{b.industry && <Badge variant="secondary">{b.industry}</Badge>}</div>
            {b.tagline && <p className="mt-1 text-sm text-muted-foreground">{b.tagline}</p>}
            {b.description && <p className="mt-2 text-sm leading-6 text-muted-foreground">{b.description}</p>}
            {b.services?.length > 0 && <div className="mt-2 flex flex-wrap gap-1.5">{b.services.map((s) => <Badge key={s} variant="secondary">{s}</Badge>)}</div>}
            {b.looking_for && <p className="mt-2 text-sm text-muted-foreground"><span className="font-medium text-foreground">Looking for:</span> {b.looking_for}</p>}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function Directory() {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    api.get("/networking/directory").then((r) => setMembers(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    const q = query.toLowerCase().trim();
    if (!q) return members;
    return members.filter((m) =>
      [m.name, m.company, m.title, m.business?.business_name, m.business?.industry, ...(m.business?.services || [])]
        .filter(Boolean).join(" ").toLowerCase().includes(q)
    );
  }, [members, query]);

  if (loading) return <SectionLoader />;

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="font-serif text-2xl font-semibold text-foreground sm:text-3xl">Member Directory</h1>
          <p className="mt-1 text-muted-foreground tabular-nums">{members.length} members in the network</p>
        </div>
        <div className="relative w-full sm:w-80">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input className="pl-9" placeholder="Search name, company, industry…" value={query} onChange={(e) => setQuery(e.target.value)} data-testid="directory-search-input" />
        </div>
      </div>

      {filtered.length === 0 ? (
        <EmptyState icon={Users} title="No members found" description="Try a different search term." />
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {filtered.map((m) => (
            <Card key={m.user_id} className="group cursor-pointer border-border bg-card transition-shadow duration-200 hover:shadow-[0_18px_50px_rgba(20,20,20,0.10)]" onClick={() => setSelected(m)} data-testid="directory-member-card">
              <CardContent className="p-5">
                <div className="flex items-center gap-3">
                  <Avatar className="h-12 w-12"><AvatarImage src={m.avatar || undefined} /><AvatarFallback className="bg-primary text-primary-foreground">{initials(m.name)}</AvatarFallback></Avatar>
                  <div className="min-w-0">
                    <p className="truncate font-semibold text-foreground">{m.name}</p>
                    <p className="truncate text-sm text-muted-foreground">{[m.title, m.company].filter(Boolean).join(" · ") || "Member"}</p>
                  </div>
                </div>
                {m.business?.business_name && (
                  <div className="mt-3 flex items-center gap-1.5 text-sm text-muted-foreground"><Briefcase className="h-3.5 w-3.5" /><span className="truncate">{m.business.business_name}</span></div>
                )}
                {m.business?.services?.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-1.5">{m.business.services.slice(0, 3).map((s) => <Badge key={s} variant="secondary" className="text-xs">{s}</Badge>)}</div>
                )}
                {m.tier === "core" && <div className="mt-3"><RoleBadges roles={m.roles} is_owner={m.is_owner} showEligible={false} size="sm" /></div>}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <MemberDialog member={selected} onClose={() => setSelected(null)} />
    </div>
  );
}
